import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  confirmResetPassword,
  confirmSignIn,
  rememberDevice,
  resetPassword,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { Button, Card, FieldError, Input, Label, ProgressBar } from '../components/ui';
import { InfoNote } from '../components/compliance';
import { DEMO } from '../lib/demo';

/** Same mark as the sidebar (layouts/AppLayout.tsx), adapted for a light background. */
function LoginLogo() {
  return (
    <div className="mb-6 flex flex-col items-center gap-2.5">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-primary-mid to-primary-dark font-serif text-2xl font-semibold text-white shadow-[0_4px_12px_-4px_rgba(138,46,60,.55)]">
        C
      </span>
      <span className="text-center font-serif text-xl font-medium leading-none text-ink">
        Cumpli
        <span className="mt-1 block font-mono text-[9px] tracking-[0.18em] text-ink-faint">
          CONSOLA AML/CFT
        </span>
      </span>
    </div>
  );
}

function PasswordInput({
  id,
  name,
  autoComplete,
  required,
  minLength,
  autoFocus,
  onChange,
}: {
  id: string;
  name: string;
  autoComplete?: string | undefined;
  required?: boolean | undefined;
  minLength?: number | undefined;
  autoFocus?: boolean | undefined;
  onChange?: ((value: string) => void) | undefined;
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        className="pr-10"
        onChange={(e) => onChange?.(e.currentTarget.value)}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink"
        tabIndex={-1}
        aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

const PASSWORD_POLICY = [
  { key: 'policyMinLength', check: (pw: string) => pw.length >= 12 },
  { key: 'policyUppercase', check: (pw: string) => /[A-Z]/.test(pw) },
  { key: 'policyLowercase', check: (pw: string) => /[a-z]/.test(pw) },
  { key: 'policyNumber', check: (pw: string) => /[0-9]/.test(pw) },
  { key: 'policySymbol', check: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
] as const;

function PasswordPolicyChecker({ value }: { value: string }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <ul className="mt-1 space-y-1">
      {PASSWORD_POLICY.map(({ key, check }) => {
        const ok = check(value);
        return (
          <li
            key={key}
            className={`flex items-center gap-1.5 text-xs ${ok ? 'text-primary-dark' : 'text-ink-faint'}`}
          >
            {ok ? <Check size={13} /> : <X size={13} />}
            {t(`auth.${key}`)}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Confiar en este dispositivo" on the MFA step. When checked, the login flow
 * calls rememberDevice() after a successful sign-in so Cognito skips the 2FA
 * challenge on the next login from this device.
 */
function TrustDeviceField({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex items-start gap-2 rounded-xl bg-canvas px-3 py-2.5 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
      />
      <span>
        {t('auth.trustDevice')}
        <span className="mt-0.5 block text-xs text-ink-faint">
          {t('auth.trustDeviceHint')}
        </span>
      </span>
    </label>
  );
}

type Step =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; uri: string; secret: string }
  | { kind: 'totpCode' }
  | { kind: 'resetRequest' }
  | { kind: 'resetConfirm'; email: string };

function stepProgress(step: Step): number {
  switch (step.kind) {
    case 'credentials':
      return 1 / 3;
    case 'newPassword':
      return 2 / 3;
    case 'totpSetup':
    case 'totpCode':
      return 1;
    case 'resetRequest':
      return 0.5;
    case 'resetConfirm':
      return 1;
  }
}

const RESET_STEPS = new Set<Step['kind']>(['resetRequest', 'resetConfirm']);

/**
 * Cognito sign-in flow (§9): credentials -> (first login: new password ->
 * TOTP enrollment with QR) -> TOTP code -> app. Plus password reset.
 */
export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired =
    (location.state as { sessionExpired?: boolean } | null)?.sessionExpired ?? false;
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  // Success banner shown on the credentials step after a password reset lands.
  const [notice, setNotice] = useState('');
  // "Confiar en este dispositivo": when checked on the MFA step, we call
  // rememberDevice() after sign-in so Cognito skips MFA next time on this device.
  const [trustDevice, setTrustDevice] = useState(false);

  async function handleNextStep(nextStep: {
    signInStep: string;
    totpSetupDetails?: unknown;
  }): Promise<void> {
    switch (nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setPasswordValue('');
        setStep({ kind: 'newPassword' });
        break;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
        // The pool's PLUS feature plan offers more than one MFA setup method
        // (TOTP + passkey/WebAuthn), so Cognito asks the user to choose. We only
        // support TOTP enrollment here — pick it and continue to the QR step,
        // instead of dead-ending on the generic error.
        const { nextStep: afterSelect } = await confirmSignIn({ challengeResponse: 'TOTP' });
        await handleNextStep(afterSelect);
        break;
      }
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const details = nextStep.totpSetupDetails as {
          sharedSecret: string;
          getSetupUri: (appName: string, account?: string) => URL;
        };
        setStep({
          kind: 'totpSetup',
          uri: details.getSetupUri('Cumpli', email).toString(),
          secret: details.sharedSecret,
        });
        break;
      }
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setStep({ kind: 'totpCode' });
        break;
      case 'DONE': {
        // If the user asked to trust this device, register it with Cognito so
        // the next sign-in from here skips the MFA challenge. Best-effort: a
        // failure here must never block a successful login.
        if (trustDevice) {
          try {
            await rememberDevice();
          } catch {
            /* device remembering is a convenience — ignore failures */
          }
        }
        // Single default landing — the Agente is the app's front door.
        navigate('/agente');
        break;
      }
      default:
        setError(t('common.genericError'));
    }
  }

  async function run(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const name = (err as { name?: string }).name ?? '';
      const msg = ((err as Error).message ?? '').toLowerCase();
      if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') {
        setError(t('auth.invalidCredentials'));
      } else if (name === 'ExpiredCodeException') {
        // Recovery/verification code has lapsed — tell the user to request a
        // fresh one rather than showing the generic "invalid code" message.
        setError(t('auth.expiredCode'));
      } else if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
        setError(t('auth.tooManyAttempts'));
      } else if (name === 'CodeMismatchException' || name === 'EnableSoftwareTokenMFAException') {
        setError(t('auth.invalidCode'));
      } else if (
        !navigator.onLine ||
        msg.includes('failed to fetch') ||
        msg.includes('load failed') ||
        msg.includes('networkerror') ||
        name === 'NetworkError'
      ) {
        setError(t('auth.networkError'));
      } else {
        setError(t('common.genericError'));
      }
    } finally {
      setBusy(false);
    }
  }

  const onCredentials = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const username = String(form.get('email'));
    setEmail(username);
    setNotice('');
    void run(async () => {
      // Clear any stale/leftover session first — otherwise Amplify throws
      // UserAlreadyAuthenticatedException when re-logging in (e.g. after the app
      // redirected here on "session expired" while old tokens still linger).
      try {
        await signOut();
      } catch {
        /* no active session to clear — fine */
      }
      const { nextStep } = await signIn({
        username,
        password: String(form.get('password')),
      });
      await handleNextStep(nextStep);
    });
  };

  const onNewPassword = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const pw = String(form.get('newPassword'));
    if (pw !== String(form.get('confirmPassword'))) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    void run(async () => {
      const { nextStep } = await confirmSignIn({ challengeResponse: pw });
      await handleNextStep(nextStep);
    });
  };

  const onTotpCode = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Strip anything that isn't a digit: authenticator apps show codes as
    // "123 456", and mobile paste/autofill commonly carries that space or a
    // trailing one, which Cognito then rejects as a code mismatch.
    const code = String(new FormData(e.currentTarget).get('code')).replace(/\D/g, '');
    void run(async () => {
      const { nextStep } = await confirmSignIn({ challengeResponse: code });
      await handleNextStep(nextStep);
    });
  };

  // Keep the 2FA field to digits only as the user types/pastes (mobile keyboards
  // and authenticator copy-paste insert spaces). Belt-and-suspenders with onTotpCode.
  const sanitizeCodeInput = (e: FormEvent<HTMLInputElement>) => {
    e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
  };

  const onResetRequest = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const username = String(new FormData(e.currentTarget).get('email'));
    void run(async () => {
      await resetPassword({ username });
      setPasswordValue('');
      setStep({ kind: 'resetConfirm', email: username });
    });
  };

  const onResetConfirm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (step.kind !== 'resetConfirm') return;
    const form = new FormData(e.currentTarget);
    void run(async () => {
      await confirmResetPassword({
        username: step.email,
        confirmationCode: String(form.get('code')),
        newPassword: String(form.get('newPassword')),
      });
      // Clean success path: back to the credentials step with a brief note so
      // the user knows the change worked and can log in with the new password.
      setPasswordValue('');
      setNotice(t('auth.passwordUpdated'));
      setStep({ kind: 'credentials' });
    });
  };

  function handleBack() {
    setError('');
    setPasswordValue('');
    if (step.kind === 'resetConfirm') {
      setStep({ kind: 'resetRequest' });
    } else {
      // Abandon any active Cognito challenge and restart from credentials.
      setStep({ kind: 'credentials' });
    }
  }

  const isResetFlow = RESET_STEPS.has(step.kind);
  const showContext = Boolean(email) && !isResetFlow && step.kind !== 'credentials';

  if (DEMO) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
        <Card className="w-full max-w-sm">
          <LoginLogo />
          <InfoNote tone="amber">
            <div>Entorno demo de ventas · sin Cognito · datos de demostración</div>
          </InfoNote>
          <p className="mb-4 mt-4 text-sm text-ink-soft">
            Entra como <strong>Ana Robles</strong> (Administrador de Entidad · Mi Éxito).
            En producción esto sería correo + contraseña + MFA (TOTP).
          </p>
          <Button className="w-full" onClick={() => navigate('/agente')}>
            Entrar (demo)
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card className="w-full max-w-sm">
        <LoginLogo />

        {sessionExpired && !notice && (
          <div className="mb-4 rounded-xl bg-tone-red-bg px-4 py-3 text-sm font-medium text-tone-red">
            Tu sesión ha expirado. Ingresa de nuevo.
          </div>
        )}

        {step.kind === 'credentials' && notice && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-primary/10 px-4 py-3 text-sm font-medium text-primary-dark">
            <Check size={16} className="shrink-0" />
            {notice}
          </div>
        )}

        <div className="mb-5">
          <ProgressBar value={stepProgress(step)} />
        </div>

        {showContext && (
          <p className="mb-4 text-sm text-ink-soft">
            {t('auth.signingInAs')} <strong className="text-ink">{email}</strong>
          </p>
        )}

        {step.kind === 'credentials' && (
          <form onSubmit={onCredentials} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.login')}</h2>
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="current-password"
                required
              />
            </div>
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('auth.signIn')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={() => {
                setError('');
                setNotice('');
                setStep({ kind: 'resetRequest' });
              }}
            >
              {t('auth.forgotPassword')}
            </button>
          </form>
        )}

        {step.kind === 'newPassword' && (
          <form onSubmit={onNewPassword} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.newPasswordTitle')}</h2>
            <div>
              <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                autoFocus
                required
                minLength={12}
                onChange={setPasswordValue}
              />
              <PasswordPolicyChecker value={passwordValue} />
            </div>
            <div>
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                required
                minLength={12}
              />
            </div>
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('common.save')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={handleBack}
            >
              {t('auth.back')}
            </button>
          </form>
        )}

        {step.kind === 'totpSetup' && (
          <form onSubmit={onTotpCode} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.setupMfaTitle')}</h2>
            <p className="whitespace-pre-line text-sm text-ink-soft">{t('auth.setupMfaHelp')}</p>
            <div className="flex justify-center">
              <QRCodeSVG value={step.uri} size={180} />
            </div>
            <div>
              <Label>{t('auth.secretKey')}</Label>
              <code className="block break-all rounded bg-canvas p-2 text-xs">{step.secret}</code>
            </div>
            <div>
              <Label htmlFor="code">{t('auth.mfaCode')}</Label>
              <Input
                id="code"
                name="code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                onInput={sanitizeCodeInput}
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
            <TrustDeviceField checked={trustDevice} onChange={setTrustDevice} />
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('auth.verify')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={handleBack}
            >
              {t('auth.back')}
            </button>
          </form>
        )}

        {step.kind === 'totpCode' && (
          <form onSubmit={onTotpCode} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.mfaTitle')}</h2>
            <p className="text-sm text-ink-soft">{t('auth.mfaHelp')}</p>
            <div>
              <Label htmlFor="code">{t('auth.mfaCode')}</Label>
              <Input
                id="code"
                name="code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                onInput={sanitizeCodeInput}
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
            <TrustDeviceField checked={trustDevice} onChange={setTrustDevice} />
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('auth.verify')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={handleBack}
            >
              {t('auth.back')}
            </button>
          </form>
        )}

        {step.kind === 'resetRequest' && (
          <form onSubmit={onResetRequest} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.resetTitle')}</h2>
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" name="email" type="email" autoFocus required />
            </div>
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('auth.resetSend')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={() => {
                setError('');
                setStep({ kind: 'credentials' });
              }}
            >
              {t('auth.backToLogin')}
            </button>
          </form>
        )}

        {step.kind === 'resetConfirm' && (
          <form onSubmit={onResetConfirm} className="space-y-4">
            <h2 className="text-lg font-semibold">{t('auth.resetNewPasswordTitle')}</h2>
            <p className="text-sm text-ink-soft">
              {t('auth.resetEmailSent')}{' '}
              <strong className="text-ink">{step.email}</strong>.{' '}
              {t('auth.resetEmailNote')}
            </p>
            <div>
              <Label htmlFor="code">{t('auth.resetCode')}</Label>
              <Input id="code" name="code" autoFocus required />
            </div>
            <div>
              <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                required
                minLength={12}
                onChange={setPasswordValue}
              />
              <PasswordPolicyChecker value={passwordValue} />
            </div>
            <FieldError message={error} />
            <Button type="submit" className="w-full" disabled={busy}>
              {t('auth.resetConfirm')}
            </Button>
            <button
              type="button"
              className="w-full text-center text-sm text-primary hover:underline"
              onClick={handleBack}
            >
              {t('auth.back')}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
